import { AudioCommand } from './AudioCommand'
import { AudioEncoding } from './AudioEncoding'
import { getNewAudioStreamConfiguration } from './index'
import { getStreamWorker } from '../ws/stream'
import { logger } from '../lib/logging'
import client from '../apollo'
import { SET_AUDIO_STREAM_FORMAT } from '../apollo/actions'

/* For testing purposes only
 * Rather than gather audio through a mic, this uses downloaded audio files as a sound source
 *
 * AudioStreamPlayer object creates a target DOM object and appends it to the document body.
 * The playing of an audio file is triggered via firing a new-audio CustomEvent on this object.
 * The audio file to be played is downloaded from a URL specified in the detail property of the custom event.
 *
 */
class AudioStreamPlayer {
  constructor () {
    // start stream worker, if it is not already started
    this.stream = getStreamWorker()

    const AudioContext = window.AudioContext || window.webkitAudioContext
    this.ctx = new AudioContext()

    this.format = {
      channels: 1,
      bitDepth: 16,
      encoding: AudioEncoding.LINEAR16,
      sampleRate: this.ctx.sampleRate,
      signed: true,
      source: 'url'
    }

    this.source = null
    this.accumulator = null
    this.ready = false

    // Create a target DOM object with eventListener to trigger audio play
    const ASP = this // scoping hack for event listener
    this.audioPlayerNode = document.createElement('div')
    this.audioPlayerNode.setAttribute('data-cy', 'audio-player')
    this.audioPlayerNode.addEventListener('new-audio', function (e) {
      // detail is expected to contain URL of audio file
      if (e.detail && e.detail.length > 0) {
        ASP.play(e.detail)
      } else {
        console.info('Audio play triggered but with no URL')
      }
    }, false)
    document.body.appendChild(this.audioPlayerNode)

    this._initialized = this._initialize()
  }

  // This breaks the promise out of the constructor so we can check for completion later
  async _initialize () {
    // Detect AudioWorklet available
    if (this._detectAudioWorklet()) {
      console.debug('WebAudio: Utilizing AudioWorklets')
      logger.info('WebAudio: Utilizing AudioWorklets')

      // Create accumulator and transfer its port
      return Promise.resolve(this.ctx.audioWorklet.addModule('/audio/AccumulationProcessor.js'))
        .then(() => {
          // eslint-disable-next-line
          this.accumulator = new AudioWorkletNode(this.ctx, 'accumulation-processor')
          try {
            this.stream.postMessage({
              cmd: AudioCommand.TRANSFER_PORT,
              port: this.accumulator.port
            }, [this.accumulator.port])
          } catch (err) {
            console.error('Unable to transfer port to StreamWorker: ', err)
          }
          this.accumulator.connect(this.ctx.destination)
          this.ready = true
        }).catch(err => {
          console.error('WebAudio: Unable to add audio worklets', err)
          logger.warn('WebAudio: Unable to add audio worklets')
        })
    } else {
      console.error("WebAudio: Can't use worklets!")
      logger.warn("WebAudio: Can't use worklets!")
    }
  }

  // Play audio file
  async play (url) {
    // The audio worklet accumulator must fully initialized prior to play
    if (!this.ready) {
      await this._initialized
    }
    // Make locals for the scope of the onload()
    const source = this.source = this.ctx.createBufferSource()
    const accumulator = this.accumulator
    const context = this.ctx
    const format = this.format
    const stream = this.stream

    // Cleanup once the audio buffer is empty (note: pipeline may not be)
    this.source.onended = function (event) {
      console.info('Source audio buffer consumed')
      source.disconnect()
    }

    // Get new audio file and fill buffer
    const request = new XMLHttpRequest()
    request.open('GET', url, true)
    request.responseType = 'arraybuffer'
    request.onload = function () {
      if (request.status === 200) {
        context.decodeAudioData(request.response, function (buffer) {
          // Update the config to report the actual URL
          format.source = url
          client.mutate({
            mutation: SET_AUDIO_STREAM_FORMAT,
            variables: { audioStreamFormat: format }
          })
          stream.postMessage({
            cmd: AudioCommand.CONFIGURATION,
            // New streamStartTime required, so pass `true` for the second parameter
            config: getNewAudioStreamConfiguration(format, true)
          })

          // Set the retrieved audio as the source buffer
          source.buffer = buffer
          source.connect(accumulator)
        },
        function (e) {
          console.info('Error with decoding audio data: ', e.err)
        })

        // Now press play
        console.info('Playing : ', url)
        source.start(0)
      } else { // Report failed request
        console.info(`Unable to download ${url}  HTTP Status: ${request.status}`)
      }
    }
    request.send()
  }

  async stop () {
    // Just in case this was called before initialization finished
    if (!this.ready) {
      await this._initialized
    }
    this.ready = false
    await this.ctx.close()
    this.source = null
    this.accumulator = null
  }

  _detectAudioWorklet () {
    const OfflineAudioContext = window.OfflineAudioContext
    const context = new OfflineAudioContext(1, 1, 44100)
    return Boolean(context.audioWorklet && typeof context.audioWorklet.addModule === 'function')
  }
}

export default AudioStreamPlayer
